import { floorAt } from '../../../../ft3d/engine/ft3d-shape-core.js';
import { roomContainsPoint } from '../../../../ft3d/engine/ft3d-creation-core.js';
import { posCoord } from '../../shared/array-types';
import { surfaceField } from '../compile/from-base-encounter-media';
import { fromBaseRecord } from '../compile/from-base-context';
import { inventoryRowList, readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers';
import { readItemField } from '../compile/item-field-reader';
import { createInventoryTemplateIdentityResolver } from '../compile/inventory-template-identity';
import { inventoryEncounterLink, inventoryEquipmentSlot, inventoryFixtureFlags, inventoryPropName, inventorySceneryShell, inventorySolid } from './solid-obstacle';
import { inventoryMeshDrawn } from './prop-mesh-space';
import { inventoryDefinition } from './definition';
import { inventoryTemplateValue } from '../movement/inventory-support';

type Row = Record<string, any>;
type Point = { x_pos: number; y_pos: number; z_pos: number };
type Span = [number, number];

/** One picker per command. Only selected cells are retained; retries replay the already planned command. */
export function generatorScatter(encounter: Row, hosts: readonly (readonly [Row, string])[], die: (sides: number) => number,
    value: (row: Row, key: string) => unknown, fallback: () => number): () => Point {
    const raw = surfaceField(encounter, 'encounter_shape');
    const shape: Row | null = typeof raw === 'string' ? JSON.parse(raw) : fromBaseRecord(raw) ? raw : null;
    const boxes: Row[] = inventoryRowList(shape?.boxes).map(node => {
        const box = node as Row;
        const mirrors = inventoryRowList(shape?.authoring?.roomFootprints).filter(f => f.roomId === box.id);
        return { ...box, footprint: fromBaseRecord(box.creation?.footprint) ? box.creation.footprint : mirrors[mirrors.length - 1] };
    });
    const span = (axis: 'x' | 'y'): Span | null => {
        if (shape) {
            if (!boxes.length) return null;
            return [Math.ceil(Math.min(...boxes.map(box => Number(box[axis]?.min))) / 5),
                Math.floor(Math.max(...boxes.map(box => Number(box[axis]?.max))) / 5)];
        }
        for (const [host, prefix] of hosts) {
            const lo = surfaceField(host, `${prefix}_range_min_${axis}`), hi = surfaceField(host, `${prefix}_range_max_${axis}`);
            if (lo != null && hi != null && Number(hi) >= Number(lo)) return [Math.ceil(Number(lo) / 5), Math.floor(Number(hi) / 5)];
            const width = Math.abs(Number(surfaceField(host, `${prefix}_max_${axis}`) ?? 0));
            if (width) return [Math.ceil(-width / 10), Math.floor(width / 10)];
        }
        return null;
    };
    const xs = span('x'), ys = span('y');
    if (!xs || !ys || ![...xs, ...ys].every(Number.isFinite) || xs[1] < xs[0] || ys[1] < ys[0]) {
        if (shape) throw new Error('generator:scatter-no-floor-cell');
        return () => ({ x_pos: fallback(), y_pos: 0, z_pos: 0 });
    }
    const key = (x: number, y: number): string => `${Math.round(x / 5)},${Math.round(y / 5)}`;
    const occupied = new Map<string, number[]>(), used = new Set<string>();
    const occupy = (node: Row, inventory: boolean): void => {
        const saved = inventory ? node.inventory : node.encounter_creatures;
        const at = (axis: 'x_pos' | 'y_pos' | 'z_pos'): number => posCoord(node, axis) ?? posCoord(saved, axis)
            ?? Number((inventory ? readInventoryField(node, axis) : surfaceField(saved ?? node, axis)) ?? 0);
        const k = key(at('x_pos'), at('y_pos')), zs = occupied.get(k) ?? [];
        zs.push(at('z_pos')); occupied.set(k, zs);
    };
    for (const node of inventoryRowList(encounter.encounter_creatures_arr) as Row[]) {
        if (!Number(surfaceField(node.encounter_creatures ?? node, 'encounter_creature_deleted') ?? 0)
            && readItemField(node, 'item_type') !== 'lair') occupy(node, false);
    }
    for (const node of readInventoryRows(encounter, 'encounter_inventories_arr')) {
        if (Number(readInventoryField(node, 'inventory_deleted') ?? 0)) continue;
        const definition = inventoryDefinition(node, encounter.catalog);
        const resolveInventoryIdentity = definition ? createInventoryTemplateIdentityResolver(() => definition) : undefined;
        let generation = readInventoryField(node, 'inventory_generation');
        if (typeof generation === 'string') { try { generation = JSON.parse(generation); } catch { generation = null; } }
        if (inventorySolid({ private: Number(readInventoryField(node, 'inventory_private') ?? 0) !== 0,
            itemType: String(readItemField(node, 'item_type', { resolveInventoryIdentity }) ?? readItemField(definition, 'item_type') ?? ''),
            name: inventoryPropName(node, definition), equipmentSlot: inventoryEquipmentSlot(node, definition),
            quantity: Number(readInventoryField(node, 'item_qty') ?? 1), encounterLink: inventoryEncounterLink(node),
            sceneryShell: inventorySceneryShell(node), wallRun: fromBaseRecord(generation) && !!generation.wallRun,
            corpseLinked: !!readInventoryField(node, 'inventory_encounter_creature_id'),
            ...inventoryFixtureFlags(k => inventoryTemplateValue(node, k, { value, definition: () => definition })),
            // A cold row without its definition keeps its cell occupied until the normal owner hydrates it.
            ...(!definition ? { immovable: true } : {}),
            meshDrawn: inventoryMeshDrawn(node, definition, node.catalog ?? encounter.catalog ?? {}) })) occupy(node, true);
    }
    const point = (cx: number, cy: number): Point | null => {
        const x = cx * 5, y = cy * 5;
        const floor = shape ? floorAt(shape, x, y) : 0;
        if (floor == null || shape && !boxes.some(box => Number(box.floor) === floor && roomContainsPoint(box as any, x, y))) return null;
        return { x_pos: x, y_pos: y, z_pos: floor };
    };
    const rank = (p: Point): number => (occupied.get(key(p.x_pos, p.y_pos))?.some(z => Math.abs(z - p.z_pos) < .01) ? 2 : 0)
        + (used.has(key(p.x_pos, p.y_pos)) ? 1 : 0);
    return () => {
        let selected: Point | null = null;
        // Uniform rejection over the union's bounding rectangle; overlaps do not get extra weight.
        for (let attempt = 0; attempt < 64; attempt++) {
            const p = point(xs[0] + die(xs[1] - xs[0] + 1) - 1, ys[0] + die(ys[1] - ys[0] + 1) - 1);
            if (p && rank(p) === 0) { selected = p; break; }
        }
        if (!selected) {
            // A repeated tape or nearly full room must still use a remaining free cell. Reservoir sampling
            // keeps this exhaustive fallback uniform without allocating/copying the floor or held world.
            let best = Infinity, count = 0;
            for (let x = xs[0]; x <= xs[1]; x++) for (let y = ys[0]; y <= ys[1]; y++) {
                const p = point(x, y); if (!p) continue;
                const score = rank(p); if (score > best) continue;
                if (score < best) { best = score; count = 0; }
                if (die(++count) === 1) selected = p;
            }
        }
        if (!selected) throw new Error('generator:scatter-no-floor-cell');
        used.add(key(selected.x_pos, selected.y_pos));
        return selected;
    };
}
